import React, { useEffect, useState, useRef, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom';
import { getBookingsList, getSalesList } from '../../APICalling/APICalling';
import styles from './Style.module.css';
import BookingFilterSidebar from './BookingFilterSidebar';
import PrimaryButton from '../../Components/Buttons/PrimaryButton';
import Loader from '../../Components/Loader/Loader';
import SearchIcon from '../../Components/Icons/SearchIcon';
import FilterIcon from '../../Components/Icons/FilterIcon';
import DotIcon from '../../Components/Icons/DotIcon';
import RefundArrowIcon from '../../Components/Icons/RefundArrowIcon';
import { formatDateIntoString } from '../../Utils/utils';
import SideBarRight from './SideBarRight';
import CheckoutSidebar from '../../Components/salesCheckout/CheckoutSidebar';
import CrossIcon from '../../Assets/images/Search Input.svg';
import DropdownNoImage from '../../Components/DropDown/DropdownNoImage';
import { useGlobalContext } from '../../Context/GlobalContext';
import ViewIcon from '../../Assets/images/view-only-icon.svg';
import CustomerDetailsSidebar from '../../Components/CustomerDetailsModal/CustomerDetailsModal.js';
import EventListner from '../../Components/Sockect/EventListner.js';
import { useMemo } from 'react';

const normalizeSaleSearchId = (value) => String(value ?? '').trim().replace(/^#/, '');

const hasExplicitSaleIdPrefix = (value) => String(value ?? '').trim().startsWith('#');

const isNumericSaleSearch = (value) => {
    const normalized = normalizeSaleSearchId(value);
    return normalized !== '' && /^\d+$/.test(normalized);
};

/** URL / Go to sale: numeric → sales_id. Manual search: only with # prefix. */
const shouldUseSalesIdParam = (trimmedSearch, fetchOptions = null) => {
    if (!isNumericSaleSearch(trimmedSearch)) return false;
    if (fetchOptions?.openSidebarFromUrlSearch) return true;
    return hasExplicitSaleIdPrefix(trimmedSearch);
};

const findBookingBySaleSearchId = (bookings, searchId) => {
    const numericSaleQuery = normalizeSaleSearchId(searchId);
    if (!numericSaleQuery) return null;
    return (bookings || []).find((booking) => {
        const saleId = normalizeSaleSearchId(booking?.sale_id);
        const bookingId = normalizeSaleSearchId(booking?.id);
        return saleId === numericSaleQuery || bookingId === numericSaleQuery;
    }) || null;
};

const Bookings = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const {
        userAssignedOutlets,
        selectedOutlet,
        setSelectedOutlet,
        loggedOutletID,
        setOutletAccessMap,
        setReference,
    } = useGlobalContext();

    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [isSideBarActive, setIsSideBarActive] = useState(false);
    const [bookings, setBookings] = useState([]);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [bookingId, setBookingId] = useState(null);
    const [bookingStatus, setBookingStatus] = useState(null);
    const [selectedSale, setSelectedSale] = useState(null);
    const [showBookingDetails, setShowBookingDetails] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const tableRef = React.useRef(null);
    const loadMoreRef = React.useRef(null);
    const pageRef = useRef(1);
    const [activeParams, setActiveParams] = useState({});
    const [appliedFilters, setAppliedFilters] = useState({});
    const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
    const isFirstRender = React.useRef(true);
    const debounceTimerRef = useRef(null);
    const searchInputRef = useRef(null);
    const [towerOptions, setTowerOptions] = useState([]);
    const canLoadMoreRef = useRef(false);
    const lastUrlSearchProcessed = useRef(null);
    const activeParamsRef = useRef({});
    const fetchBookingsListRef = useRef(null);
    const socketRefreshTimerRef = useRef(null);
    const suppressNextAutoLoadRef = useRef(false);
    const silentRefreshRequestIdRef = useRef(0);
    const [isCustomerDetailsOpen, setIsCustomerDetailsOpen] = useState(false);
    const [selectedCustomerForDetails, setSelectedCustomerForDetails] = useState(null);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const noop = useCallback(() => {}, []);
    const emptyBookingsList = useMemo(() => [], []);

    const closeSideBar = useCallback(() => {
        setIsSideBarActive(false);
        if (searchParams.has('search') || searchParams.has('outlet')) {
            const nextSearchParams = new URLSearchParams(searchParams);
            nextSearchParams.delete('search');
            nextSearchParams.delete('outlet');
            setSearchParams(nextSearchParams, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    const openSideBar = useCallback((sale) => {
        const bookingIdentifier = sale?.booking_id || sale?.id || sale?.booking?.id || '';
        const numericId = String(bookingIdentifier).replace('#', '');
        setBookingId(numericId);
        setBookingStatus(sale?.status || sale?.payment_status);
        setSelectedSale(sale || null);
        setShowBookingDetails(true);
        setIsSideBarActive(true);
    }, []);

    const handleCheckoutSaleCompleted = useCallback((sale) => {
        setIsCheckoutOpen(false);
        openSideBar(sale);
    }, [openSideBar]);

    const formatDDMMYYYY = (dateInput) => {
        if (!dateInput) return '';
        const d = new Date(dateInput);
        if (Number.isNaN(d.getTime())) return '';
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${dd}-${mm}-${yyyy}`;
    };

    // Helper function to get current date info
    const getCurrentDateInfo = () => {
        const now = new Date();
        const today = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

        const thisWeekStart = new Date(now);
        const dayOfWeek = now.getDay();
        const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        thisWeekStart.setDate(now.getDate() - daysFromMonday);
        const thisWeekEnd = new Date(thisWeekStart);
        thisWeekEnd.setDate(thisWeekStart.getDate() + 6);
        const thisWeek = `${thisWeekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${thisWeekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

        const lastWeekStart = new Date(thisWeekStart);
        lastWeekStart.setDate(thisWeekStart.getDate() - 7);
        const lastWeekEnd = new Date(lastWeekStart);
        lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
        const lastWeek = `${lastWeekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${lastWeekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

        const twoWeeksAgoStart = new Date(lastWeekStart);
        twoWeeksAgoStart.setDate(lastWeekStart.getDate() - 7);
        const lastTwoWeeks = `${twoWeeksAgoStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${lastWeekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

        const currentMonth = now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
        const previousMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
        const twoMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 2, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

        return { today, yesterday, thisWeek, lastWeek, lastTwoWeeks, currentMonth, previousMonth, twoMonthsAgo };
    };

    // Helper function to convert predefined date ranges to actual date ranges
    const convertDateRange = (dateRangeText) => {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const dayOfWeek = today.getDay();
        const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

        switch (dateRangeText) {
            case `Today (${getCurrentDateInfo().today})`:
                return {
                    start: today.toLocaleDateString('en-GB').split('/').reverse().join('-'),
                    end: today.toLocaleDateString('en-GB').split('/').reverse().join('-')
                };

            case `Yesterday (${getCurrentDateInfo().yesterday})`:
                const yesterday = new Date(today);
                yesterday.setDate(yesterday.getDate() - 1);
                return {
                    start: yesterday.toLocaleDateString('en-GB').split('/').reverse().join('-'),
                    end: yesterday.toLocaleDateString('en-GB').split('/').reverse().join('-')
                };

            case `This Week (${getCurrentDateInfo().thisWeek})`:
                const startOfWeek = new Date(today);
                startOfWeek.setDate(today.getDate() - daysFromMonday);
                const endOfWeek = new Date(startOfWeek);
                endOfWeek.setDate(startOfWeek.getDate() + 6);
                return {
                    start: startOfWeek.toLocaleDateString('en-GB').split('/').reverse().join('-'),
                    end: endOfWeek.toLocaleDateString('en-GB').split('/').reverse().join('-')
                };

            case `Last Week (${getCurrentDateInfo().lastWeek})`:
                const lastWeekStart = new Date(today);
                lastWeekStart.setDate(today.getDate() - daysFromMonday - 7);
                const lastWeekEnd = new Date(lastWeekStart);
                lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
                return {
                    start: lastWeekStart.toLocaleDateString('en-GB').split('/').reverse().join('-'),
                    end: lastWeekEnd.toLocaleDateString('en-GB').split('/').reverse().join('-')
                };

            case `Last Two Weeks (${getCurrentDateInfo().lastTwoWeeks})`:
                const twoWeeksStart = new Date(today);
                twoWeeksStart.setDate(today.getDate() - daysFromMonday - 14);
                const twoWeeksEnd = new Date(today);
                twoWeeksEnd.setDate(today.getDate() - daysFromMonday - 1);
                return {
                    start: twoWeeksStart.toLocaleDateString('en-GB').split('/').reverse().join('-'),
                    end: twoWeeksEnd.toLocaleDateString('en-GB').split('/').reverse().join('-')
                };

            case getCurrentDateInfo().currentMonth:
                const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
                const currentMonthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                return {
                    start: currentMonthStart.toLocaleDateString('en-GB').split('/').reverse().join('-'),
                    end: currentMonthEnd.toLocaleDateString('en-GB').split('/').reverse().join('-')
                };

            case getCurrentDateInfo().previousMonth:
                const prevMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                const prevMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
                return {
                    start: prevMonthStart.toLocaleDateString('en-GB').split('/').reverse().join('-'),
                    end: prevMonthEnd.toLocaleDateString('en-GB').split('/').reverse().join('-')
                };

            case getCurrentDateInfo().twoMonthsAgo:
                const twoMonthsStart = new Date(today.getFullYear(), today.getMonth() - 2, 1);
                const twoMonthsEnd = new Date(today.getFullYear(), today.getMonth() - 1, 0);
                return {
                    start: twoMonthsStart.toLocaleDateString('en-GB').split('/').reverse().join('-'),
                    end: twoMonthsEnd.toLocaleDateString('en-GB').split('/').reverse().join('-')
                };

            default:
                return null;
        }
    };

    const mapFilterStatusToApi = (status) => {
        if (!status || status === 'All') return '';
        return String(status).trim().toLowerCase();
    };

    const isSaleFilterStatus = (status) => {
        return ['open', 'closed', 'open(in progress)'].includes(String(status || '').trim().toLowerCase());
    };

    const buildParamsFromFilters = (filters) => {
        const params = {};

        // Sale # → sales_id
        if (filters?.saleAt && String(filters.saleAt).trim() !== '') {
            params.sales_id = String(filters.saleAt).trim();
        }

        // Amount range
        const fromAmt = parseFloat(filters?.amountFrom);
        if (!Number.isNaN(fromAmt)) params.from_amt = fromAmt;
        const toAmt = parseFloat(filters?.amountTo);
        if (!Number.isNaN(toAmt)) params.to_amt = toAmt;

        // Status
        const apiStatus = mapFilterStatusToApi(filters?.status);
        if (apiStatus) params.status = apiStatus;

        // Type
        if (apiStatus === 'refunded') {
            params.type = 'refund';
        } else if (isSaleFilterStatus(filters?.status)) {
            params.type = 'sale';
        }

        // Payment methods
        if (Array.isArray(filters?.paymentMethods) && filters.paymentMethods.length > 0) {
            params.payment_method = filters.paymentMethods.map((m) => String(m).toUpperCase()).join(',');
        }

        // Date range
        if (filters?.saleDate && filters.saleDate !== 'All Time') {
            if (filters.saleDate === 'Custom Time Period') {
                const start = formatDDMMYYYY(filters?.saleDateStart);
                const end = formatDDMMYYYY(filters?.saleDateEnd);
                if (start) params.start_date = start;
                if (end) params.end_date = end;
            } else {
                // Handle predefined date ranges
                const dateRange = convertDateRange(filters.saleDate);
                if (dateRange) {
                    params.start_date = dateRange.start;
                    params.end_date = dateRange.end;
                }
            }
        }

        // Includes staff (therapist)
    if (filters?.includeStaffEmpty) {
        params.therapist_id = '';
    } else if (Array.isArray(filters?.includeStaff) && filters.includeStaff.length > 0) {
        params.therapist_id = filters.includeStaff.join(',');
    }

        // Client: prefer explicit user_id from sidebar selection; fallback to numeric clientSearch
        if (filters?.user_id && String(filters.user_id).trim() !== '') {
            params.user_id = String(filters.user_id).trim();
        } else if (filters?.clientSearch && /^\d+$/.test(String(filters.clientSearch).trim())) {
            params.user_id = String(filters.clientSearch).trim();
        }

        return params;
    };

    const fetchBookingsList = useCallback(async (currentPage = 1, overrideParams = null, searchValue = null, fetchOptions = null) => {
        const isSilent = fetchOptions?.silent === true;
        try {
            if (currentPage === 1) {
                if (!isSilent) {
                    setInitialLoading(true);
                }
                // Disable infinite scroll until initial load completes
                canLoadMoreRef.current = false;
            } else if (!isSilent) {
                setLoading(true);
            }
            // Use overrideParams if provided, otherwise use activeParams for subsequent pages
            const paramsToUse = overrideParams !== null ? overrideParams : activeParams;
            const baseItem = {
                pagination: 1,
                per_page: 20,
                page: currentPage,
                outlet: selectedOutlet, // Add outlet to payload
                ...paramsToUse,
            };
            let requestItem = baseItem;
            const trimmedSearch = searchValue !== null ? String(searchValue).trim() : (search ? String(search).trim() : '');
            if (trimmedSearch !== '') {
                const numericId = normalizeSaleSearchId(trimmedSearch);
                if (shouldUseSalesIdParam(trimmedSearch, fetchOptions)) {
                    requestItem = { ...baseItem, sales_id: numericId };
                } else {
                    requestItem = { ...baseItem, search_text: trimmedSearch };
                }
            }
            // Use new Sales API (keep old API available, not removed)
            const response = await getSalesList(requestItem);
            const list = response?.data?.data?.list || {};
            const allBookings = list.bookings || [];
            const paginationData = list.pagination || {};

            if (currentPage === 1) {
                setBookings(allBookings);
                if (fetchOptions?.openSidebarFromUrlSearch && allBookings.length > 0) {
                    let rowToOpen = null;
                    if (shouldUseSalesIdParam(trimmedSearch, fetchOptions)) {
                        rowToOpen = findBookingBySaleSearchId(allBookings, trimmedSearch);
                        if (!rowToOpen && allBookings.length === 1) {
                            rowToOpen = allBookings[0];
                        }
                    } else if (allBookings.length === 1) {
                        rowToOpen = allBookings[0];
                    }
                    if (rowToOpen) {
                        openSideBar(rowToOpen);
                    }
                }
                // Enable infinite scroll after a delay to prevent immediate triggering
                setTimeout(() => {
                    canLoadMoreRef.current = true;
                }, 500);
            } else {
                setBookings((prev) => [...prev, ...allBookings]);
            }

            if (!paginationData || paginationData.currentPage >= paginationData.lastPage || allBookings.length === 0) {
                setHasMore(false);
            } else {
                setHasMore(true);
            }

        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("ERROR in fetchBookingsList():", error);
        } finally {
            if (!isSilent) {
                setLoading(false);
                setInitialLoading(false);
            }
        }
    }, [activeParams, search, selectedOutlet, openSideBar]);

    const buildSalesListRequestItem = useCallback((pageNum, overrideParams = null, searchValue = null, fetchOptions = null) => {
        const paramsToUse = overrideParams !== null ? overrideParams : activeParams;
        const baseItem = {pagination: 1, per_page: 20,page: pageNum,outlet: selectedOutlet,...paramsToUse,};
        const trimmedSearch = searchValue !== null ? String(searchValue).trim() : (search ? String(search).trim() : '');
        if (trimmedSearch === '') return baseItem;

        const numericId = normalizeSaleSearchId(trimmedSearch);
        if (shouldUseSalesIdParam(trimmedSearch, fetchOptions)) {
            return { ...baseItem, sales_id: numericId };
        }
        return { ...baseItem, search_text: trimmedSearch };
    }, [activeParams, search, selectedOutlet]);

    /** Silent refresh: refetch all currently loaded pages so scroll depth is preserved. */
    const silentRefreshLoadedPages = useCallback(async () => {
        const loadedPages = Math.max(1, Number(pageRef.current) || 1);
        const scrollTop = tableRef.current?.scrollTop ?? 0;
        const requestId = (silentRefreshRequestIdRef.current || 0) + 1;
        silentRefreshRequestIdRef.current = requestId;

        canLoadMoreRef.current = false;
        suppressNextAutoLoadRef.current = true;

        try {
            const pageNumbers = Array.from({ length: loadedPages }, (_, i) => i + 1);
            const settled = await Promise.allSettled(
                pageNumbers.map((pageNum) =>
                    getSalesList(buildSalesListRequestItem(pageNum, activeParams, search))
                )
            );

            // Ignore stale responses if a newer silent refresh started
            if (silentRefreshRequestIdRef.current !== requestId) return;

            const successfulResponses = settled
                .filter((result) => result.status === 'fulfilled')
                .map((result) => result.value);

            settled.forEach((result, index) => {
                if (result.status === 'rejected') {
                    // eslint-disable-next-line no-console
                    console.error(
                        `ERROR in silentRefreshLoadedPages() page ${pageNumbers[index]}:`,
                        result.reason
                    );
                }
            });

            // Keep existing list if every page request failed
            if (successfulResponses.length === 0) {
                canLoadMoreRef.current = true;
                return;
            }

            const merged = [];
            const seen = new Set();
            let lastPagination = {};

            successfulResponses.forEach((response) => {
                const list = response?.data?.data?.list || {};
                const pageBookings = list.bookings || [];
                lastPagination = list.pagination || lastPagination;
                pageBookings.forEach((row) => {
                    const key = normalizeSaleSearchId(row?.sale_id ?? row?.id);
                    if (key) {
                        if (seen.has(key)) return;
                        seen.add(key);
                    }
                    merged.push(row);
                });
            });

            setBookings(merged);

            const apiLastPage = Number(lastPagination?.lastPage) || 1;
            const nextPage = Math.min(loadedPages, apiLastPage);
            pageRef.current = nextPage;
            setPage(nextPage);
            setHasMore(nextPage < apiLastPage);

            requestAnimationFrame(() => {
                if (tableRef.current) {
                    tableRef.current.scrollTop = scrollTop;
                }
                setTimeout(() => {
                    canLoadMoreRef.current = true;
                }, 500);
            });
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("ERROR in silentRefreshLoadedPages():", error);
            canLoadMoreRef.current = true;
        }
    }, [activeParams, search, buildSalesListRequestItem]);

    const refreshBookingsFromStart = useCallback((fetchOptions = null) => {
        const isSilent = fetchOptions?.silent === true;
        if (isSilent) {
            silentRefreshLoadedPages();
            return;
        }
        pageRef.current = 1;
        setPage(1);
        setBookings([]);
        setHasMore(true);
        fetchBookingsList(1, activeParams, search, fetchOptions);
    }, [activeParams, search, fetchBookingsList, silentRefreshLoadedPages]);

    const loadNextBookingsPage = useCallback(() => {
        const nextPage = pageRef.current + 1;
        pageRef.current = nextPage;
        setPage(nextPage);
        fetchBookingsList(nextPage);
    }, [fetchBookingsList]);


    // Keep refs updated with latest values
    useEffect(() => {
        pageRef.current = page;
    }, [page]);

    useEffect(() => {
        activeParamsRef.current = activeParams;
    }, [activeParams]);

    useEffect(() => {
        fetchBookingsListRef.current = fetchBookingsList;
    }, [fetchBookingsList]);

    // Initialize tower options from user assigned outlets (only edit permission)
    useEffect(() => {
        if (userAssignedOutlets?.length) {
            // Filter to only show outlets with edit permission
            const editOutlets = userAssignedOutlets.filter((outlet) => outlet.mode === "edit");

            const mapped = editOutlets.map((outlet) => ({
                label: (
                    <span>
                        {outlet.name}
                        {outlet.mode === "edit" && " (Edit)"}
                    </span>
                ),
                value: outlet.id,
                data: outlet,
            }));

            setTowerOptions(mapped);

            // Check if there is an outlet param in URL
            const urlOutletId = searchParams.get('outlet');
            const urlOutlet = urlOutletId ? editOutlets.find(o => String(o.id) === String(urlOutletId)) : null;

            if (urlOutlet) {
                 if (selectedOutlet !== urlOutlet.id) {
                     setSelectedOutlet(urlOutlet.id);
                 }
                 const nextSearchParams = new URLSearchParams(searchParams);
                 if (nextSearchParams.has('outlet')) {
                     nextSearchParams.delete('outlet');
                     setSearchParams(nextSearchParams, { replace: true });
                 }
            } else if (urlOutletId) {
                const nextSearchParams = new URLSearchParams(searchParams);
                nextSearchParams.delete('outlet');
                setSearchParams(nextSearchParams, { replace: true });
            } else {
                // Check if current selected outlet has edit permission
                const currentOutletHasEdit = editOutlets.some((o) => o.id === selectedOutlet);
    
                // If no outlet selected OR current outlet doesn't have edit permission, set default
                if (!selectedOutlet || !currentOutletHasEdit) {
                    // Prefer loggedOutletID if it has edit permission, otherwise use first edit outlet
                    const loggedOutletHasEdit = editOutlets.some((o) => o.id === loggedOutletID);
                    if (loggedOutletHasEdit) {
                        setSelectedOutlet(loggedOutletID);
                    } else if (editOutlets.length > 0) {
                        setSelectedOutlet(editOutlets[0].id);
                    }
                }
            }

            // Set outlet access map for access control
            const accessMap = {};
            userAssignedOutlets.forEach((o) => {
                accessMap[o.id] = o.mode; // "edit" or "view"
            });
            if (setOutletAccessMap) {
                setOutletAccessMap(accessMap);
            }
        }
    }, [userAssignedOutlets, loggedOutletID, setSelectedOutlet, setOutletAccessMap, searchParams, setSearchParams]);

    const handleTowerSelect = (selectedOption) => {
        setSelectedOutlet(selectedOption?.value);
    };

    // Fetch bookings when outlet changes
    useEffect(() => {
        if (selectedOutlet) {
            pageRef.current = 1;
            setPage(1);
            setBookings([]);
            setHasMore(true);
            setInitialLoading(true);
            // Reset filters when outlet changes
            setActiveParams({});
            setAppliedFilters({});
            fetchBookingsList(1, {});
        }
    }, [selectedOutlet]);

    // Refresh list when a sale completes from sidebar flow
    useEffect(() => {
        const handler = () => {
            refreshBookingsFromStart();
        };
        window.addEventListener('salesPaymentSuccess', handler);
        return () => window.removeEventListener('salesPaymentSuccess', handler);
    }, [refreshBookingsFromStart]);

    // Refresh list when a sale is voided from SideBarRight
    useEffect(() => {
        const handler = () => {
            refreshBookingsFromStart();
        };
        window.addEventListener('voidSaleCompleted', handler);
        return () => window.removeEventListener('voidSaleCompleted', handler);
    }, [refreshBookingsFromStart]);

    // Silent refresh list when socket update received
    useEffect(() => {
        const handler = () => {
            if (socketRefreshTimerRef.current) {
                clearTimeout(socketRefreshTimerRef.current);
            }
            socketRefreshTimerRef.current = setTimeout(() => {
                suppressNextAutoLoadRef.current = true;
                refreshBookingsFromStart({ silent: true });
            }, 300);
        };
        window.addEventListener('refreshBookingList', handler);
        return () => {
            window.removeEventListener('refreshBookingList', handler);
            if (socketRefreshTimerRef.current) {
                clearTimeout(socketRefreshTimerRef.current);
            }
        };
    }, [refreshBookingsFromStart]);

    // Remove sale from list when socket delete event received
    useEffect(() => {
        const handler = (e) => {
            const saleId = e?.detail?.sale_id;
            if (!saleId) return;

            const matchesSale = (row) =>
                String(row?.sale_id ?? row?.id ?? '').replace(/^#/, '') === String(saleId);

            setBookings((prev) => prev.filter((row) => !matchesSale(row)));

            if (selectedSale && matchesSale(selectedSale)) {
                closeSideBar();
                setSelectedSale(null);
                setBookingId(null);
                setShowBookingDetails(false);
            }
        };
        window.addEventListener('saleDeleted', handler);
        return () => window.removeEventListener('saleDeleted', handler);
    }, [selectedSale, closeSideBar]);

    // Customer details modal event listener
    useEffect(() => {
        const handleOpenCustomerDetails = (e) => {
            const customer = e.detail?.customer;
            if (customer) {
                setSelectedCustomerForDetails(customer);
                setIsCustomerDetailsOpen(true);
            }
        };
        window.addEventListener('openCustomerDetails', handleOpenCustomerDetails);
        return () => {
            window.removeEventListener('openCustomerDetails', handleOpenCustomerDetails);
        };
    }, []);

    // Handle URL search parameter -> update local state and trigger search
    useEffect(() => {
        const urlSearch = searchParams.get('search') || '';

        // Only process if:
        // 1. URL has a search parameter
        // 2. It's different from what we last processed
        if (urlSearch && lastUrlSearchProcessed.current !== urlSearch) {
            // Mark this URL search as processed
            lastUrlSearchProcessed.current = urlSearch;

            // Clear param to avoid re-trigger on refresh
            const nextSearchParams = new URLSearchParams(searchParams);
            if (nextSearchParams.has('search')) {
                nextSearchParams.delete('search');
                setSearchParams(nextSearchParams, { replace: true });
            }

            // Clear any pending debounced searches
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }

            // Always update the search state to reflect URL
            setSearch(urlSearch);

            // Add a small delay to ensure component is fully mounted and ready
            setTimeout(() => {
                // Trigger search for URL parameters
                pageRef.current = 1;
                setPage(1);
                setBookings([]);
                setHasMore(true);
                setInitialLoading(true);

                // Use current refs to get latest values and avoid stale closures
                const currentActiveParams = activeParamsRef.current;
                const currentFetch = fetchBookingsListRef.current;

                // Use activeParams to maintain filters with URL search
                if (currentFetch) {
                    currentFetch(1, currentActiveParams, urlSearch, { openSidebarFromUrlSearch: true });
                }
            }, 500); // 500ms delay for smooth transition
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    // Cleanup debounce timer on unmount
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    // Restore focus after initial loading completes (not while sale/payment sidebar is open)
    useEffect(() => {
        if (!initialLoading && searchInputRef.current && !isSideBarActive && !isCheckoutOpen) {
            // Small delay to ensure DOM is stable
            setTimeout(() => {
                if (isSideBarActive || isCheckoutOpen) return;
                if (searchInputRef.current && document.activeElement !== searchInputRef.current) {
                    searchInputRef.current.focus();
                }
            }, 10);
        }
    }, [initialLoading, isSideBarActive, isCheckoutOpen]);

    // Debounced search function
    const debouncedSearch = useCallback((searchValue) => {
        // Clear any existing timeout
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        // Set a new timeout for the search
        debounceTimerRef.current = setTimeout(() => {
            // Store focus state before API call
            const wasFocused = document.activeElement === searchInputRef.current;

            // Reset pagination when searching
            pageRef.current = 1;
            setPage(1);
            setBookings([]);
            setHasMore(true);
            setInitialLoading(true); // Set initial loading for search
            // Use activeParams to maintain filters while searching
            fetchBookingsList(1, activeParams, searchValue);

            // Restore focus after state updates
            if (wasFocused) {
                setTimeout(() => {
                    if (searchInputRef.current) {
                        searchInputRef.current.focus();
                    }
                }, 0);
            }
        }, 1000); // 1000ms delay - allows typing 1-2 words
    }, [fetchBookingsList, activeParams]);

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearch(value);

        // Reset the URL search tracker when user manually changes search
        // This allows the same URL search to work again if needed
        if (value === '') {
            lastUrlSearchProcessed.current = null;
        }

        // If search is empty, search immediately (restore filters if any)
        if (value.trim() === '') {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }

            // Store focus state before API call
            const wasFocused = document.activeElement === searchInputRef.current;

            pageRef.current = 1;
            setPage(1);
            setBookings([]);
            setHasMore(true);
            setInitialLoading(true);
            // Use activeParams (filters) when clearing search
            fetchBookingsList(1, activeParams, '');

            // Restore focus after state updates
            if (wasFocused) {
                setTimeout(() => {
                    if (searchInputRef.current) {
                        searchInputRef.current.focus();
                    }
                }, 0);
            }
        } else {
            // Use debounced search for non-empty values
            debouncedSearch(value);
        }
    };

    useEffect(() => {
        const sentinel = loadMoreRef.current;
        if (!sentinel) return;

        const rootEl = tableRef.current || null;
        const prefetchPx = Math.max(600, Math.round((rootEl?.clientHeight || 800) * 1.0));
        const observer = new IntersectionObserver(
            (entries) => {
                const first = entries[0];
                if (first.isIntersecting && hasMore && !loading && !initialLoading && canLoadMoreRef.current) {
                    loadNextBookingsPage();
                }
            },
            {
                root: rootEl, // observe within scroll container if present, else viewport
                rootMargin: `0px 0px ${prefetchPx}px 0px`,
                threshold: 0.01,
            }
        );

        observer.observe(sentinel);
        return () => {
            observer.disconnect();
        };
    }, [hasMore, loading, initialLoading, loadNextBookingsPage]);

    // Fallback scroll listener in case IntersectionObserver doesn't fire on some browsers/containers
    useEffect(() => {
        const el = tableRef.current;
        if (!el) return;

        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = el;
            const prefetchPx = Math.max(600, Math.round(clientHeight * 1.0));
            const nearBottom = scrollTop + clientHeight >= scrollHeight - prefetchPx;
            if (nearBottom && hasMore && !loading && !initialLoading && canLoadMoreRef.current) {
                loadNextBookingsPage();
            }
        };

        el.addEventListener('scroll', handleScroll);
        return () => el.removeEventListener('scroll', handleScroll);
    }, [hasMore, loading, initialLoading, loadNextBookingsPage]);


    // Remove full-page loader to prevent page blurring
    // if (initialLoading) return <Loader fullPage />;


    const getClientName = (booking) => {
        if (
            booking.booking_item &&
            Object.keys(booking.booking_item).length > 0
        ) {
            const firstKey = Object.keys(booking.booking_item)[0];
            const firstItem = booking.booking_item[firstKey][0];
            return firstItem?.customer_name || '';
        }
        return '';
    };

    const getSaleStatusMeta = (rawStatus) => {
        const normalized = String(rawStatus || '').trim().toLowerCase();
        const label = String(rawStatus || '').trim() || '—';
        let color = '#CED4D8';
        if (normalized === 'pending') color = '#B0D9E9';
        if (normalized === 'partial' || normalized === 'open(in progress)') color = '#FFE9A0';
        if (normalized === 'paid' || normalized === 'completed' || normalized === 'closed') color = '#CED4D8';
        if (normalized === 'refunded') color = '#CED4D8';
        return { label, color };
    };

    /** First column label: type + status + sale id (see product/sales list). */
    const getSalesListRowLabel = (b) => {
        const rawId = b?.sale_id ?? b?.id;
        const idStr = String(rawId ?? '').replace(/^#/, '').trim();
        if (!idStr) return '—';
        const status = String(b?.status || b?.payment_status || '').trim().toLowerCase();
        const type = String(b?.type || '').trim().toLowerCase();
        const isRefunded = status === 'refunded';
        if (type === 'membership') {
            return isRefunded ? `Refund # ${idStr}` : `Top-up # ${idStr}`;
        }
        if (type === 'booking' || type === 'product' || type ==='voucher') {
            return isRefunded ? `Refund # ${idStr}` : `Sale # ${idStr}`;
        }
        return String(b?.id ?? '').startsWith('#') ? String(b.id) : `#${idStr}`;
    };

    const isRefundSaleRow = (b) => String(b?.status || b?.payment_status || '').trim().toLowerCase() === 'refunded';

    const renderSalesListRowLabel = (b) => {
        const label = getSalesListRowLabel(b);

        if (!isRefundSaleRow(b)) {
            return highlightMatch(label, search);
        }

        return (
            <span className={styles.refundSaleLabel}>
                {highlightMatch(label, search)}
                <RefundArrowIcon size={12} color="currentColor" aria-hidden="true" />
            </span>
        );
    };

    // Server-side search: rely on API 'search_text'; no client-side filtering
    const filteredBookings = bookings;

    // Auto-load more pages if filtered result is too small and there are more pages
    useEffect(() => {
        if (suppressNextAutoLoadRef.current) {
            suppressNextAutoLoadRef.current = false;
            return;
        }

        if (!initialLoading && !loading && hasMore && filteredBookings.length < 10) {
            loadNextBookingsPage();
        }
    }, [filteredBookings.length, initialLoading, loading, hasMore, loadNextBookingsPage]);

    // Highlight helper: bolds matched query parts in a case-insensitive way
    const escapeRegExp = (str) => String(str).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const highlightMatch = (text, query) => {
        const source = text == null ? '' : String(text);
        const q = query == null ? '' : String(query).trim();
        if (!q) return source;
        try {
            const regex = new RegExp(`(${escapeRegExp(q)})`, 'ig');
            const parts = source.split(regex);
            return parts.map((part, idx) => {
                if (!part) return null;
                return regex.test(part) ? (<strong key={idx}>{part}</strong>) : (<React.Fragment key={idx}>{part}</React.Fragment>);
            });
        } catch (e) {
            return source;
        }
    };

    // Check if any filters are applied
    const hasActiveFilters = () => {
        if (!appliedFilters || Object.keys(appliedFilters).length === 0) return false;

        if (appliedFilters.saleAt && String(appliedFilters.saleAt).trim() !== '') return true;
        if (appliedFilters.amountFrom && String(appliedFilters.amountFrom).trim() !== '') return true;
        if (appliedFilters.amountTo && String(appliedFilters.amountTo).trim() !== '') return true;
        if (appliedFilters.status && appliedFilters.status !== 'All') return true;
        if (Array.isArray(appliedFilters.paymentMethods) && appliedFilters.paymentMethods.length > 0) return true;
        if (appliedFilters.saleDate && appliedFilters.saleDate !== 'All Time') return true;
        // Only count includeStaff as active if not all therapists are selected
        // Note: We can't check therapists here, but if includeStaff is empty array, it means all selected (no filter)
        if (Array.isArray(appliedFilters.includeStaff) && appliedFilters.includeStaff.length > 0) return true;
    if (appliedFilters.includeStaffEmpty) return true;
        if (appliedFilters.user_id && String(appliedFilters.user_id).trim() !== '') return true;
        return false;
    };




    return (
        <>
            <EventListner 
                setBookingsList={noop} 
                setNewAppointmentSlots={noop}
                bookingsList={emptyBookingsList}
                onBreakCreated={noop}
                onCloseSidebar={noop}
                onScheduleUpdated={noop}
            />
            <div className={`${styles.bookingsContainer} header-sapce`}>
                {/* Header */}
                <div className={`${styles.header} container-fluid`}>
                    {/* Left Section - Dropdown */}
                    <div className={styles.leftSection}>
                        <DropdownNoImage
                            options={towerOptions}
                            selectedLabel={
                                towerOptions.find(
                                    (tower) => tower.value === selectedOutlet
                                )?.label || ""
                            }
                            onSelect={handleTowerSelect}
                            dropdownClass={`${styles.towerDropdown} headerOutletFilter topHeaderDropdown`}
                            dropdownMenuClass={`${styles.towerDropdownMenu} outletFilterList`}
                            placeholder="List Towers"
                            placeholderClass={styles.placeholder}
                            labelText=""
                        />
                    </div>

                    {/* Right Section - Search, Options and New Checkout */}
                    <div className={styles.rightSection}>
                        <div className={`${styles.InputGroup}`}>
                            <SearchIcon />
                            <input
                                ref={searchInputRef}
                                className={styles.searchInput}
                                type="text"
                                name='search'
                                placeholder="Search by #sale id or client name"
                                value={search}
                                onChange={handleSearchChange}
                                autoComplete="off"
                            />
                            {search.trim().length > 0 && (
                                <button
                                    className={styles.clearSearchButton}
                                    onClick={() => {
                                        setSearch('');
                                        pageRef.current = 1;
                                        setPage(1);
                                        setBookings([]);
                                        setHasMore(true);
                                        setInitialLoading(true); // Set initial loading for clear search
                                        // Use activeParams to maintain filters when clearing search
                                        fetchBookingsList(1, activeParams, '');
                                        // Restore focus after clearing
                                        setTimeout(() => {
                                            if (searchInputRef.current) {
                                                searchInputRef.current.focus();
                                            }
                                        }, 0);
                                    }}
                                    type="button"
                                    title="Clear search"
                                >
                                    <img src={CrossIcon} alt="Clear" />
                                </button>
                            )}
                        </div>
                        
                        <div className={styles.desktopActions}>
                            <div className={`${styles.filterButton} ${styles.activeFilter}`} onClick={() => setIsFilterOpen(true)}>
                                Options
                                <FilterIcon />
                                {hasActiveFilters() && (
                                    <DotIcon size={10} color="#FF0000" className={styles.filterIndicator} />
                                )}
                            </div>
                            <PrimaryButton
                                className={styles.newCheckoutButton}
                                onClick={() => {
                                    setIsCheckoutOpen(true);
                                    setReference("");
                                }}
                                text="New Checkout"
                            />
                        </div>

                        {/* Mobile Hamburger Button */}
                        <button 
                            className={styles.hamburgerButton}
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            aria-label="Menu"
                        >
                            <span className={styles.hamburgerLine}></span>
                            <span className={styles.hamburgerLine}></span>
                            <span className={styles.hamburgerLine}></span>
                        </button>

                        {/* Mobile Dropdown Menu */}
                        {isMobileMenuOpen && (
                            <div className={styles.mobileMenuDropdown}>
                                {/* Tower/Outlet Selector in Mobile Menu */}
                                <DropdownNoImage
                                    options={towerOptions}
                                    selectedLabel={
                                        towerOptions.find(
                                            (tower) => tower.value === selectedOutlet
                                        )?.label || ""
                                    }
                                    onSelect={(opt) => {
                                        handleTowerSelect(opt);
                                        setIsMobileMenuOpen(false);
                                    }}
                                    dropdownClass={`${styles.towerDropdown} headerOutletFilter topHeaderDropdown`}
                                    dropdownMenuClass={`${styles.towerDropdownMenu} outletFilterList`}
                                    placeholder="List Towers"
                                    placeholderClass={styles.placeholder}
                                    labelText=""
                                />

                                <div 
                                    className={`${styles.filterButton} ${styles.activeFilter}`} 
                                    onClick={() => {
                                        setIsFilterOpen(true);
                                        setIsMobileMenuOpen(false);
                                    }}
                                >
                                    Options
                                    <FilterIcon />
                                    {hasActiveFilters() && (
                                        <DotIcon size={10} className={styles.filterIndicator} />
                                    )}
                                </div>
                                <PrimaryButton
                                    className={styles.newCheckoutButton}
                                    onClick={() => {
                                        setIsCheckoutOpen(true);
                                        setIsMobileMenuOpen(false);
                                    }}
                                    text="New Checkout"
                                />
                            </div>
                        )}
                    </div>
                </div>



                {/* Table */}
                <div className={`${styles.tableContainer} container-fluid`} ref={tableRef}>
                    {initialLoading ? (
                        <div className={styles.loaderWrapper}>
                            <Loader />
                        </div>
                    ) : (
                        <table>
                            <thead>
                                <tr>
                                    <th>Sales #</th>
                                    <th>Status</th>
                                    <th>Date</th>
                                    <th>Client</th>
                                    <th>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredBookings.map((b, idx) => {
                                    const { label, color } = getSaleStatusMeta(b.status || b.payment_status);
                                    return (
                                        <tr
                                            key={`${b.id ?? 'booking'}-${b.created_at ?? ''}-${b.updated_at ?? ''}-${idx}`} onClick={() => openSideBar(b)} className={styles.clickableRow}
                                        >
                                            <td className={styles.colSales}>{renderSalesListRowLabel(b)}</td>
                                            <td className={styles.colStatus}>
                                                <span
                                                    style={{
                                                        backgroundColor: color,
                                                    }}
                                                ></span>
                                                <div className={styles.status}>
                                                    {label}
                                                </div>
                                            </td>
                                            <td className={styles.colDate}>
                                                {b.created_at ? formatDateIntoString(b.created_at) : (
                                                    b.booking_item && Object.keys(b.booking_item).length > 0
                                                        ? formatDateIntoString(b.booking_item[Object.keys(b.booking_item)[0]][0]?.service_at)
                                                        : '—'
                                                )}
                                            </td>
                                            <td className={styles.colClient}>
                                                {highlightMatch(b.user || getClientName(b) || '—', search)}
                                            </td>
                                            <td className={styles.colTotal}>{b.total ? `$${b.total}` : (b.grand_total ? `$${b.grand_total}` : '—')}</td>
                                        </tr>
                                    )
                                })}
                                {filteredBookings.length === 0 && !initialLoading && (
                                    <tr>
                                        <td colSpan={5} className={styles.emptyMessage}>
                                            No sales found.
                                        </td>
                                    </tr>
                                )}
                                {/* Sentinel row for IntersectionObserver */}
                                <tr>
                                    <td colSpan={5}>
                                        <div ref={loadMoreRef} className={styles.loadMoreSentinel} />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
            <BookingFilterSidebar
                isOpen={isFilterOpen}
                onClose={() => setIsFilterOpen(false)}
                onApply={(f, options = {}) => {
                    const params = buildParamsFromFilters(f || {});
                    // Clear activeParams first, then set new ones
                    setActiveParams({});
                    if (Object.keys(params).length > 0) {
                        setActiveParams(params);
                    }
                    setAppliedFilters(f || {}); // Store the applied filters
                    pageRef.current = 1;
                    setPage(1);
                    setHasMore(true);
                    fetchBookingsList(1, params);
                    if (!options.keepOpen) {
                        setIsFilterOpen(false);
                    }
                }}
                currentFilters={appliedFilters}
            />
            {isSideBarActive && (
                <div
                    className={styles.sidebarOverlay}
                    onClick={closeSideBar}
                />
            )}
            <div
                className={styles.sideBarWrapper}
                onClick={(e) => e.stopPropagation()}
            >
                <SideBarRight
                    active={isSideBarActive}
                    onClose={closeSideBar}
                    id={bookingId}
                    status={bookingStatus}
                    sale={selectedSale}
                    showBookingDetails={showBookingDetails}
                    onOpenSale={openSideBar}

                />
            </div>
            <CheckoutSidebar
                open={isCheckoutOpen}
                onClose={() => setIsCheckoutOpen(false)}
                onSaleCompleted={handleCheckoutSaleCompleted}
            />

            {/* Customer Details Modal */}
            <CustomerDetailsSidebar
                customer={selectedCustomerForDetails}
                isOpen={isCustomerDetailsOpen}
                onClose={() => {
                    setIsCustomerDetailsOpen(false);
                    setSelectedCustomerForDetails(null);
                }}
            />
        </>
    )
}

export default Bookings